/**
 * Dynamic pizza sizes — FRONTEND.md §14.6 / BACKEND.md §14.6.
 *
 * Ported from legacy `lib/pizza-config-store.ts`. It lives in `packages/shared`
 * because two modules need it: the pizza configurator in `manager-menu` and the
 * pizza builder inside the order builder in `manager-orders`.
 *
 * The load-bearing part is `ingredientSizeMap`. A pizza ingredient carries its
 * prices twice: in the **legacy `price32`/`price50` columns**, which are NOT
 * NULL and therefore always present, and in the **`size_data` JSONB**, which is
 * where a franchise's real sizes live once it has more than two. `size_data`
 * overlays the legacy pair, field by field — not wholesale — because a
 * `size_data` entry may set a price and say nothing about weight.
 *
 * Every rule here about a missing value meaning "enabled" exists because
 * configurations saved before that flag was added do not carry it. Reading
 * absence as "off" would switch a live restaurant's sizes off.
 */

export interface PizzaSize {
  cm: number
  label: string
  basePrice: number
  /** Dough, sauce and base cheese — for the margin calculation. Optional. */
  baseCost?: number
  /** Missing means enabled: configs predate this flag. */
  enabled?: boolean
}

export interface PizzaBase {
  value: string
  label: string
  prices: Record<number, number>
}

export interface PizzaConfig {
  sizes: PizzaSize[]
  garlicCrustEnabled: boolean
  garlicCrustPrice: number
  /** Size used for a kombo pizza. Missing means the largest enabled size. */
  komboSizeCm?: number
  /** Missing means enabled: configs predate this flag. */
  komboEnabled?: boolean
  /**
   * How many pizzas one kombo holds - the number of selects the builders draw. `FRONTEND.md` §9.798, owner O147.
   * Missing means 3, the box every franchise had before the setting existed. Read through `komboPizzaCount`.
   */
  komboMaxPizzas?: number
  /**
   * The fewest pizzas a kombo may be added with. Missing means the maximum, which is an exact count: `min === max`
   * is how an exact box is stored, so there is no separate mode to disagree with it.
   */
  komboMinPizzas?: number
  bases: PizzaBase[]
  /**
   * The base a new pizza opens with — `FRONTEND.md` §9.60, owner decision third round.
   *
   * Names a `PizzaBase.value`. Missing, empty, or naming a base that is not in the list means **no
   * preselection**, which is what every config did before this field existed.
   */
  defaultBaseValue?: string | null
}

export interface IngredientSizeValues {
  price: number
  weightG: number
  unitLabel: string | null
  cost: number | null
}

/** What an ingredient row looks like, from either storage generation. */
export interface PizzaIngredientLike {
  price32?: number | string | null
  price50?: number | string | null
  weightG32?: number | null
  weightG50?: number | null
  unitLabel32?: string | null
  unitLabel50?: string | null
  cost32?: number | string | null
  cost50?: number | string | null
  sizeData?: Record<
    string,
    { price?: number; weightG?: number; unitLabel?: string | null; cost?: number | null }
  > | null
}

export const DEFAULT_PIZZA_CONFIG: PizzaConfig = {
  sizes: [
    { cm: 32, label: '32 cm', basePrice: 7.9 },
    { cm: 50, label: '50 cm', basePrice: 11.9 },
  ],
  garlicCrustEnabled: true,
  garlicCrustPrice: 0.5,
  bases: [
    { value: 'rajcinovy', label: 'Rajčinový základ', prices: { 32: 0, 50: 0 } },
    { value: 'smotanovy', label: 'Smotanový základ', prices: { 32: 0, 50: 0 } },
  ],
}

/**
 * Whether this size can be offered - §9.647, owner report.
 *
 * **Three things, and each is a different failure.** A non-finite `cm` was already refused. A blank `label`
 * renders as the word **undefined** - the owner's own report, *"velkosti pizze are showing really wierd sizes"* -
 * and `NewOrderPage` builds an order line as `${product.name} – ${size.label}`, so it reaches the **kitchen
 * ticket**. A non-finite `basePrice` is the pizza's own price, so the total becomes `NaN` or nought: a size
 * nobody can be charged for correctly is not a size.
 */
export function isOfferableSize(size: PizzaSize | null | undefined): boolean {
  if (!size) return false
  return (
    Number.isFinite(size.cm) &&
    typeof size.label === 'string' &&
    size.label.trim() !== '' &&
    Number.isFinite(size.basePrice)
  )
}

/**
 * Enabled sizes, smallest first. A size with no `enabled` flag counts as on.
 *
 * **Nothing is substituted when none is offerable - §9.647.** A default size carries a default **price**, and
 * inventing one is a claim about what a pizza costs. The builders say the size is not configured instead, which
 * is true and points at the screen that fixes it.
 */
export function enabledSizes(config: PizzaConfig | null | undefined): PizzaSize[] {
  return (config?.sizes ?? [])
    .filter((size) => isOfferableSize(size) && size.enabled !== false)
    .sort((left, right) => left.cm - right.cm)
}

export function isKomboEnabled(config: PizzaConfig | null | undefined): boolean {
  return config?.komboEnabled !== false
}

/** The configured kombo size, or the largest enabled one, or 50. */
export function komboSizeCm(config: PizzaConfig | null | undefined): number {
  if (config?.komboSizeCm && Number.isFinite(config.komboSizeCm)) return config.komboSizeCm
  const sizes = enabledSizes(config)
  return sizes.length > 0 ? (sizes[sizes.length - 1]?.cm ?? 50) : 50
}

/** The fewest pizzas a kombo can be set to hold: one pizza is not a kombo. */
export const KOMBO_PIZZAS_FEWEST = 2
/** The most a kombo can be set to hold. */
export const KOMBO_PIZZAS_MOST = 8
/** The box every franchise had before §9.798: three pizzas, exactly. */
export const DEFAULT_KOMBO_PIZZAS = 3

/** How many pizzas a kombo holds: at least `min`, at most `max`. Exact when the two are equal. */
export interface KomboCount {
  min: number
  max: number
}

function wholeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value))
}

/**
 * How many pizzas this franchise's kombo holds - `FRONTEND.md` §9.798, owner O147.
 *
 * **Both absent is 3 and 3**, so a franchise that never opens the setting keeps the box it had. A missing or
 * unreadable minimum means *exactly the maximum*. Both are clamped to 2..8, and the minimum to the maximum - a
 * stored `min` above `max` would otherwise be a box nobody can ever add.
 */
export function komboPizzaCount(config: PizzaConfig | null | undefined): KomboCount {
  const storedMax = wholeNumber(config?.komboMaxPizzas)
  const max = clamp(storedMax ?? DEFAULT_KOMBO_PIZZAS, KOMBO_PIZZAS_FEWEST, KOMBO_PIZZAS_MOST)
  const storedMin = wholeNumber(config?.komboMinPizzas)
  const min = clamp(storedMin ?? max, KOMBO_PIZZAS_FEWEST, max)
  return { min, max }
}

/** `3×` for an exact box, `2-4×` for a range - the count on every kombo tile and builder header. */
export function komboCountLabel(count: KomboCount): string {
  return count.min === count.max ? `${count.max}×` : `${count.min}-${count.max}×`
}

/** `"Vlastná pizza – 45 cm (…)"` → `45`. */
export function sizeCmFromName(name: string): number | null {
  const match = /(\d{2,3})\s*cm/i.exec(name)
  if (!match?.[1]) return null
  const cm = Number.parseInt(match[1], 10)
  return Number.isFinite(cm) ? cm : null
}

function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  return Number.isFinite(Number(value)) ? Number(value) : null
}

/**
 * Every size this ingredient has values for: the legacy 32/50 pair first, then
 * `size_data` laid over it field by field.
 */
export function ingredientSizeMap(
  ingredient: PizzaIngredientLike,
): Map<number, IngredientSizeValues> {
  const bySize = new Map<number, IngredientSizeValues>()

  const price32 = toNumberOrNull(ingredient.price32)
  if (price32 !== null || ingredient.weightG32 != null) {
    bySize.set(32, {
      price: price32 ?? 0,
      weightG: ingredient.weightG32 ?? 0,
      unitLabel: ingredient.unitLabel32 ?? null,
      cost: toNumberOrNull(ingredient.cost32),
    })
  }

  const price50 = toNumberOrNull(ingredient.price50)
  if (price50 !== null || ingredient.weightG50 != null) {
    bySize.set(50, {
      price: price50 ?? 0,
      weightG: ingredient.weightG50 ?? 0,
      unitLabel: ingredient.unitLabel50 ?? null,
      cost: toNumberOrNull(ingredient.cost50),
    })
  }

  for (const [key, entry] of Object.entries(ingredient.sizeData ?? {})) {
    const cm = Number.parseInt(key, 10)
    if (!Number.isFinite(cm)) continue

    const existing = bySize.get(cm)
    bySize.set(cm, {
      price: entry.price ?? existing?.price ?? 0,
      weightG: entry.weightG ?? existing?.weightG ?? 0,
      // `undefined` means "said nothing"; an explicit null means "cleared".
      unitLabel: entry.unitLabel !== undefined ? (entry.unitLabel ?? null) : (existing?.unitLabel ?? null),
      cost: entry.cost !== undefined ? (entry.cost ?? null) : (existing?.cost ?? null),
    })
  }

  return bySize
}

/**
 * The ingredient's values at one size: an exact match, otherwise the nearest
 * size that has data. Ties go to the larger size.
 *
 * The fallback is what keeps a franchise's 45 cm pizza priceable when its
 * ingredients were only ever costed at 32 and 50 — and it is why the legacy
 * NOT NULL columns can still be derived when neither 32 nor 50 is enabled.
 */
export function ingredientForSize(
  ingredient: PizzaIngredientLike,
  cm: number,
): IngredientSizeValues {
  const bySize = ingredientSizeMap(ingredient)

  const exact = bySize.get(cm)
  if (exact) return exact

  let nearest: { cm: number; values: IngredientSizeValues } | null = null
  for (const [candidateCm, values] of bySize) {
    const candidateDistance = Math.abs(candidateCm - cm)
    if (!nearest) {
      nearest = { cm: candidateCm, values }
      continue
    }
    const nearestDistance = Math.abs(nearest.cm - cm)
    if (
      candidateDistance < nearestDistance ||
      (candidateDistance === nearestDistance && candidateCm > nearest.cm)
    ) {
      nearest = { cm: candidateCm, values }
    }
  }

  return nearest?.values ?? { price: 0, weightG: 0, unitLabel: null, cost: null }
}

/** A base saved before per-size pricing carried a single `price`. */
export function migrateBases(bases: PizzaBase[]): PizzaBase[] {
  return bases.map((base) => {
    if (base.prices) return base
    const legacyPrice = (base as unknown as { price?: number }).price ?? 0
    return { value: base.value, label: base.label, prices: { 32: legacyPrice, 50: legacyPrice } }
  })
}

/**
 * Whether this base can be offered to a customer - §9.647, FB-975.
 *
 * **A base the customer cannot name is not a choice.** The owner's Šereď config held three entries with blank
 * labels, and the builder drew three identical buttons reading nothing but `+0,00 €`: a pizza whose base cannot
 * be picked, because the three options are indistinguishable.
 *
 * `value` is required too, and for a sharper reason than tidiness: it is what the draft stores, what
 * `basePriceCents` prices and what the order payload carries, so a base without one is a button that cannot be
 * recorded even if somebody presses it.
 */
export function isOfferableBase(base: PizzaBase | null | undefined): boolean {
  if (!base) return false
  return (
    typeof base.value === 'string' &&
    base.value.trim() !== '' &&
    typeof base.label === 'string' &&
    base.label.trim() !== ''
  )
}

/**
 * Fills a stored config out to a complete one, migrating what needs it.
 *
 * **Unusable bases are dropped before the empty check rather than after it - §9.647.** The fallback used to read
 * `length === 0`, so an empty list was handled and three nameless entries were not, although from the
 * customer's side they are the same failure. Entry by entry, so a config with two real bases and one blank row
 * keeps the two; the fallback is reached only when nothing offerable is left.
 */
export function normalisePizzaConfig(stored: Partial<PizzaConfig> | null | undefined): PizzaConfig {
  const merged = { ...DEFAULT_PIZZA_CONFIG, ...(stored ?? {}) }
  /* §9.647 - **no base is invented, which the owner's correction forced.** The first version of this fell back
   * to `DEFAULT_PIZZA_CONFIG.bases`, and the owner answered: *"They dont have these two at all. They have:
   * Pomodoro pelato základ and Smotanovo bryndzový základ"*. `pizzaLineName` puts the label on the order line
   * the kitchen reads, so a substituted name is a wrong base on a ticket - and unlike a blank button, a
   * plausible wrong name is not visibly broken. An empty list is the honest answer; `basesFor` reads the
   * ingredient rows, which is where a franchise can actually set one. */
  merged.bases = migrateBases((merged.bases ?? []).filter(isOfferableBase))
  return merged
}
